<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getPollTemplate, pollTemplateStates } from '@/services/pollTemplateService.js'

import Loading from '@/components/Generics/Loadings/Loading.vue'
import ButtonTornaAllaLista from '@/components/Generics/Buttons/ButtonTornaAllaLista.vue'
import QuestionGroup from '@/components/PollTemplate/Components/QuestionGroup.vue'

import { client } from '@/services/fetch.js'
import { toast } from '@/services/toast.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
  faChartLine,
  faClipboardList,
  faXmark,
  faCheck,
  faTrash,
  faPencil,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons'

library.add(faChartLine)
library.add(faClipboardList)
library.add(faXmark)
library.add(faTrash)
library.add(faCheck)
library.add(faPencil)
library.add(faArrowLeft)

const router = useRouter()
const route = useRoute()

const props = defineProps(['updatePageTitle'])

const loading = ref(true)
const currentPoll = ref(null)
const revisions = ref([])
const deletePoll = ref(false)
const activePoll = ref(false)

const canEdit = ref(false)
const canView = ref(false)
const canDelete = ref(false)

onMounted(async () => {
  if (route.meta.permissions) {
    canEdit.value = route.meta.permissions.canEdit ?? false
    canDelete.value = route.meta.permissions.canDelete ?? false
    canView.value = route.meta.permissions.canView ?? false
  }

  if (!canView.value) {
    return
  }

  currentPoll.value = await getPollTemplate(route.params.id, { complete: 1 })
  props.updatePageTitle('Template - ' + currentPoll.value.title)
  loading.value = false
})

async function attivaQuestionario() {

  let poll = currentPoll.value
  let pollTemplateActive = {
    title: poll.title,
    description: poll.description,
    tags: poll.tags,
    state: 'active'
  }

  try {
    let url = `/config/templates/polls/api/poll-template/${currentPoll.value.id}`
    currentPoll.value = await client.put(url, pollTemplateActive)
    activePoll.value = false
    toast.create('Template attivato con successo', 'success')
  } catch (error) {
    toast.create('Errore durante attivazione template, riprova', 'error')
  }
}

async function eliminaPoll() {
  try {
    loading.value = true
    let url = `/config/templates/polls/api/poll-template/${currentPoll.value.id}`
    await client.delete(url)
    router.back()
    loading.value = false
    toast.create('Template eliminato con successo', 'success')
  } catch (error) {
    loading.value = false
    toast.create('Errore durante l\'eliminazione, riprova', 'error')
  }
}

</script>

<template>
  <div>

    <loading v-if="loading"></loading>

    <div v-else-if="!loading && currentPoll">
      <div class="card shadow-none">
        <div class="card-header">
          <div class="d-flex justify-content-end" v-if="!deletePoll && !activePoll">
            <button v-if="currentPoll['state'] === 'draft' && canEdit"
                    class="btn btn-outline-danger btn-sm ms-2"
                    title="Elimina questionario"
                    @click="activePoll = false; deletePoll = true;">
              <font-awesome-icon icon="fa-solid fa-trash" />
            </button>
            <button v-if="currentPoll['state'] === 'draft' && canEdit"
                    class="btn btn-outline-primary btn-sm ms-2"
                    title="Attiva questionario"
                    @click="activePoll = true; deletePoll = false;">
              <font-awesome-icon icon="fa-solid fa-check" />
            </button>

            <router-link :to="{name: 'admin/PollTemplateStats' , params: {id: currentPoll.id}}">
              <div v-if="currentPoll['state'] !== 'draft' && (canView || canEdit)">
                <button
                  type="button"
                  class="btn btn-primary btn-sm ms-2"
                  title="Mostra statistiche"
                  @click="() => {$forceUpdate()}"
                >
                  <font-awesome-icon icon="fa-solid fa-chart-line" />
                </button>
              </div>
            </router-link>

            <div v-if="revisions.length > 1 && currentPoll['revisionNumber'] >= 1 && (canView || canEdit)">
              <div class="dropdown">
                <ul class="dropdown-menu dropdown-menu-sm dropdown-menu-end">
                  <li>
                    <button v-if="revisions.length<=0" class="dropdown-item text-center" disabled>
                      <div class="spinner-grow spinner-grow-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div v-if="!deletePoll && activePoll"
               class="alert alert-warning d-flex justify-content-sm-end align-content-center align-items-center mb-0">
            <span class="fw-bold text-dark">Vuoi attivare il questionario?</span>
            <button class="btn btn-primary btn-sm ms-3 me-2" @click="attivaQuestionario()">
              <span>Conferma</span>
            </button>
            <button class="btn btn-outline-secondary btn-sm" title="Annulla" @click="activePoll = false">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>

          <div v-if="deletePoll && !activePoll"
               class="alert alert-warning d-flex justify-content-sm-end align-content-center align-items-center mb-0">
            <span class="fw-bold text-dark">Vuoi eliminare il questionario?</span>
            <button class="btn btn-danger btn-sm me-2 ms-3" @click="eliminaPoll()">
              <span>Elimina</span>
            </button>
            <button class="btn btn-outline-secondary btn-sm" title="Annulla" @click="deletePoll = false">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>

        </div>
        <div class="card-body">
          <div>
            <div class="container-fluid">
              <div class="row mb-2">
                <div class="col-6 px-0">
                  <span>
                    <b>Stato</b>:
                    {{pollTemplateStates[currentPoll.state]}}
                  </span>
                </div>
                <div class="col-6 px-0">
                  <span>
                    <b>Revisione corrente</b>: {{ currentPoll.revisionNumber }}
                  </span>
                </div>
              </div>
              <div class="row">
                <div class="col-12 px-0">
                  <span class="d-block">
                      <b>Descrizione</b>:
                  </span>
                  <span>
                    {{ currentPoll.description }}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <question-group
                v-for="(questionGroup, index) in currentPoll['questionGroups']"
                :key="index"
                :poll-template="currentPoll"
                :question-group="questionGroup"
                :can-view="canView"
                :can-edit="canEdit"
                :can-delete="canDelete"
              >
              </question-group>

            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex justify-content-between mt-3">
      <button-torna-alla-lista @click="router.back()"></button-torna-alla-lista>
    </div>
  </div>
</template>

<style scoped>
</style>
